import { Controller } from "@hotwired/stimulus"

/**
 * Product Selector Controller
 * Handles product filtering and selection in ROI recommendations
 */
export default class extends Controller {
  static targets = [
    "productList",
    "familyFilter",
    "energyMinFilter",
    "energyMaxFilter",
    "selectedProduct",
    "selectedDisplay",
    "productCard",
  ]

  static values = {
    assessmentId: String,
    selectedId: String,
  }

  // CSS classes configurable via data attributes
  static classes = ["selected"]

  connect() {
    this.setupKeyboardNavigation()
  }

  /**
   * Setup keyboard navigation for product cards
   */
  setupKeyboardNavigation() {
    this.element.addEventListener("keydown", this.handleKeydown.bind(this))
  }

  /**
   * Handle keyboard navigation
   */
  handleKeydown(event) {
    const cards = Array.from(this.element.querySelectorAll("[data-product-id]"))
    if (cards.length === 0) return

    const currentCard = document.activeElement?.closest("[data-product-id]")
    const currentIndex = currentCard ? cards.indexOf(currentCard) : -1

    let nextIndex = currentIndex
    let shouldHandle = false

    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        nextIndex = currentIndex < cards.length - 1 ? currentIndex + 1 : 0
        shouldHandle = true
        break
      case "ArrowLeft":
      case "ArrowUp":
        nextIndex = currentIndex > 0 ? currentIndex - 1 : cards.length - 1
        shouldHandle = true
        break
      case "Enter":
      case " ":
        if (currentCard) {
          event.preventDefault()
          this.select({ currentTarget: currentCard })
        }
        return
      case "Home":
        nextIndex = 0
        shouldHandle = true
        break
      case "End":
        nextIndex = cards.length - 1
        shouldHandle = true
        break
      default:
        return
    }

    if (shouldHandle) {
      event.preventDefault()
      const nextCard = cards[nextIndex]
      if (nextCard) {
        // Focus the card or a focusable element within it
        const focusTarget =
          nextCard.querySelector("button, [tabindex]") || nextCard
        focusTarget.focus()
      }
    }
  }

  /**
   * Apply filters and reload products via Turbo
   */
  filter() {
    const params = new URLSearchParams()

    if (this.hasFamilyFilterTarget && this.familyFilterTarget.value) {
      params.set("family", this.familyFilterTarget.value)
    }

    if (this.hasEnergyMinFilterTarget && this.energyMinFilterTarget.value) {
      params.set("min_energy", this.energyMinFilterTarget.value)
    }

    if (this.hasEnergyMaxFilterTarget && this.energyMaxFilterTarget.value) {
      params.set("max_energy", this.energyMaxFilterTarget.value)
    }

    // Build URL with filters
    const url = new URL(window.location.href)
    url.search = params.toString()

    // Use Turbo to navigate
    window.Turbo.visit(url.toString(), { action: "replace" })
  }

  /**
   * Mark product as selected
   */
  select(event) {
    // Prevent default for keyboard events (space key would scroll)
    if (event.type === "keydown") {
      event.preventDefault()
    }

    const productId = event.currentTarget.dataset.productId

    if (this.hasSelectedProductTarget) {
      this.selectedProductTarget.value = productId
    }

    this.selectedIdValue = productId

    // Update visual selection and ARIA attributes
    this.element.querySelectorAll("[data-product-id]").forEach((card) => {
      const isSelected = card.dataset.productId === productId

      // Update ARIA selected state
      card.setAttribute("aria-selected", isSelected ? "true" : "false")

      // Update visual selection using Stimulus classes
      if (isSelected) {
        this.selectedClasses.forEach((cls) => card.classList.add(cls))
      } else {
        this.selectedClasses.forEach((cls) => card.classList.remove(cls))
      }
    })

    // Announce selection to screen readers
    this.announceSelection(event.currentTarget)
  }

  /**
   * Announce selection to screen readers via live region
   */
  announceSelection(card) {
    const productName =
      card.querySelector("h4")?.textContent?.trim() || "Product"
    let announcement = document.getElementById("product-selection-announcement")

    if (!announcement) {
      announcement = document.createElement("div")
      announcement.id = "product-selection-announcement"
      announcement.setAttribute("role", "status")
      announcement.setAttribute("aria-live", "polite")
      announcement.className = "sr-only"
      document.body.appendChild(announcement)
    }

    announcement.textContent = `${productName} selected`
  }

  /**
   * Clear all filters
   */
  clearFilters() {
    if (this.hasFamilyFilterTarget) {
      this.familyFilterTarget.value = ""
    }
    if (this.hasEnergyMinFilterTarget) {
      this.energyMinFilterTarget.value = ""
    }
    if (this.hasEnergyMaxFilterTarget) {
      this.energyMaxFilterTarget.value = ""
    }

    this.filter()
  }

  /**
   * Show product details (expand card)
   */
  showDetails(event) {
    const productId = event.currentTarget.dataset.productId
    const card = this.element.querySelector(`[data-product-id="${productId}"]`)

    if (card) {
      card.classList.toggle("expanded")
      // Could trigger a modal or expand the card here
    }
  }
}
